import { useCallback, useEffect, useState } from "react";
import { collection, getCountFromServer } from "firebase/firestore";
import { db } from "@/lib/firebase";

export interface PlatformCounts {
  orgs: number | null;
  studios: number | null;
  endUsers: number | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

interface PlatformCountsState {
  orgs: number | null;
  studios: number | null;
  endUsers: number | null;
  loading: boolean;
  error: string | null;
}

const INITIAL_STATE: PlatformCountsState = {
  orgs: null,
  studios: null,
  endUsers: null,
  loading: true,
  error: null,
};

/**
 * The platform dashboard's three existing aggregate counts (orgs, studios,
 * endUsers), unchanged from the prior PlatformDashboard implementation.
 * Runs once on mount and again only on an explicit `refresh()` call — no
 * polling or timers. `null` means "unavailable" (loading or errored);
 * callers must not substitute 0 for it.
 */
export function usePlatformCounts(): PlatformCounts {
  const [state, setState] = useState<PlatformCountsState>(INITIAL_STATE);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    Promise.all([
      getCountFromServer(collection(db, "orgs")),
      getCountFromServer(collection(db, "studios")),
      getCountFromServer(collection(db, "endUsers")),
    ])
      .then(([orgsSnap, studiosSnap, endUsersSnap]) => {
        if (cancelled) return;
        setState({
          orgs: orgsSnap.data().count,
          studios: studiosSnap.data().count,
          endUsers: endUsersSnap.data().count,
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch dashboard counts:", err);
        setState({
          orgs: null,
          studios: null,
          endUsers: null,
          loading: false,
          error: "Failed to load platform counts.",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  const refresh = useCallback(() => setRefreshToken((t) => t + 1), []);

  return { ...state, refresh };
}
